import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import Popover from 'react-bootstrap/Popover';
import { Search, X } from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';

import { Filter } from '../../../Enum/Filters';
import { Period } from '../../../Interface/Interest/Period';

import { filterChanged } from '../../../Store/Filter/action';
import useGetFilterValue from '../../../Hook/Interest/UseGetFilterValue';
import useGetPeriods from '../../../Hook/Interest/UseGetPeriods';
import {
    arePeriodsEqual,
    getIdentifier,
    getLabel,
    sortByPeriod
} from '../../../Helpers/Interest/PeriodHelper';

const MAX_AMOUNT_SELECTED_PERIODS = 8;

interface State {
    keyword: string;
    showPopover: boolean;
}

const FixedPeriodsFilter: React.FC = () => {
    const dispatch = useDispatch();
    const periods = useGetPeriods();

    const [state, setState] = useState<State>({ keyword: '', showPopover: false });

    const selectedPeriods = useGetFilterValue(Filter.FixedPeriods) as Period[];

    const togglePeriod = useCallback(
        (toggledPeriod: Period): void => {
            const dispatchFilter = (periodsToDispatch: Period[]): void => {
                periodsToDispatch.sort(sortByPeriod);
                dispatch(filterChanged(Filter.FixedPeriods, periodsToDispatch));
            };

            if (!selectedPeriods.find(period => arePeriodsEqual(period, toggledPeriod))) {
                dispatchFilter([...selectedPeriods, toggledPeriod]);
            } else {
                dispatchFilter(selectedPeriods.filter(period => !arePeriodsEqual(period, toggledPeriod)));
            }

            if (selectedPeriods.length === MAX_AMOUNT_SELECTED_PERIODS) {
                setState({ ...state, showPopover: false });
            }
        },
        [dispatch, selectedPeriods],
    );

    const filteredPeriods = periods.filter(period => getIdentifier(period).includes(state.keyword.toLowerCase()));

    return (
        <>
            <Form className="popover-select-form" autoComplete="off">
                <Form.Group controlId="fixed-periods">
                    <Form.Label>Rentevastperiodes</Form.Label>
                    <InputGroup>
                        <InputGroup.Prepend
                            className={classNames({ disabled: selectedPeriods.length === MAX_AMOUNT_SELECTED_PERIODS })}
                        >
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                            type="text"
                            className="rounded-right"
                            required
                            placeholder="Voeg periodes toe.."
                            disabled={selectedPeriods.length === MAX_AMOUNT_SELECTED_PERIODS}
                            onChange={(e: any) => setState({ ...state, keyword: e.target.value })}
                            onFocus={() => setState({ ...state, showPopover: true })}
                        />
                        {filteredPeriods.length > 0 &&
                            state.showPopover &&
                            selectedPeriods.length < MAX_AMOUNT_SELECTED_PERIODS && (
                                <>
                                    <div
                                        className="popover-backdrop"
                                        onClick={() => setState({ ...state, showPopover: false })}
                                        onKeyDown={() => setState({ ...state, showPopover: false })}
                                    />
                                    <Popover id="fixed-periods-popover" className="select" content placement="bottom">
                                        {filteredPeriods.map(period => (
                                            <p
                                                key={`available-${getIdentifier(period)}`}
                                                onClick={() => togglePeriod(period)}
                                                onKeyDown={() => togglePeriod(period)}
                                                className={classNames({
                                                    active: selectedPeriods.find(
                                                        selectedPeriod => arePeriodsEqual(selectedPeriod, period),
                                                    ),
                                                })}
                                            >
                                                {getLabel(period)}
                                                {selectedPeriods.find(selectedPeriod =>
                                                    arePeriodsEqual(selectedPeriod, period)) && <X />}
                                            </p>
                                        ))}
                                    </Popover>
                                </>
                            )}
                    </InputGroup>
                    {selectedPeriods.length === MAX_AMOUNT_SELECTED_PERIODS && (
                        <p className="popover-select-form__max-amount-selected">
                            Maximaal {MAX_AMOUNT_SELECTED_PERIODS} periodes
                        </p>
                    )}
                    {selectedPeriods.map(period => (
                        <div className="selected-value-wrapper" key={`selected-${getIdentifier(period)}`}>
                            <div
                                className="selected-value-wrapper__value"
                                onClick={() => togglePeriod(period)}
                                onKeyDown={() => togglePeriod(period)}
                            >
                                {getLabel(period)}
                                <X />
                            </div>
                        </div>
                    ))}
                </Form.Group>
            </Form>
        </>
    );
};

export default FixedPeriodsFilter;
